import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import StartGameOverlay from './StartGameOverlay';
import RetroLoadingDots from './RetroLoadingDots';

const RetroPixelDrawer = ({ 
  isActive = false, 
  onEnd,
  onGameComplete,
  showStartButton = false,
  isGenerating = false 
}) => {
  const [pixels, setPixels] = useState(new Set());
  const [currentColor, setCurrentColor] = useState('#FF00FF');
  const [isDrawing, setIsDrawing] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(10);
  // Show start button only when explicitly requested (like in RetroArcade)
  const [waitingToStart, setWaitingToStart] = useState(showStartButton);
  const containerRef = useRef(null);
  const gridSize = 12; // 12x12 grid

  const colors = [
    '#FF00FF', '#FF6B00', '#00FF9F', '#FF1493',
    '#FFB800', '#00FFE0', '#FF4D00', '#FF8A00'
  ];

  // Initialize game
  useEffect(() => {
    if (isActive && !gameStarted && !waitingToStart) {
      setGameStarted(true);
      setPixels(new Set());
      setTimeLeft(10);
    }
  }, [isActive, waitingToStart]);

  // Game timer - only runs when game has actually started
  useEffect(() => {
    if (!gameStarted || waitingToStart) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          onGameComplete?.({
            type: 'pixeldrawer',
            pixels: Array.from(pixels),
            title: 'Pixel Art Results',
            message: 'Your pixel art masterpiece is complete!'
          });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [gameStarted, waitingToStart, pixels]);

  const handleStart = () => {
    setWaitingToStart(false);
  };

  const handleMouseDown = (e) => {
    if (!gameStarted) return;
    setIsDrawing(true);
    const pixel = getPixelFromEvent(e);
    if (pixel) {
      const pixelKey = `${pixel.x},${pixel.y}`;
      setPixels(prev => {
        const next = new Set(prev);
        next.add(`${pixelKey}:${currentColor}`);
        return next;
      });
    }
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || !gameStarted) return;
    const pixel = getPixelFromEvent(e);
    if (pixel) {
      const pixelKey = `${pixel.x},${pixel.y}`;
      setPixels(prev => {
        const next = new Set(prev);
        next.add(`${pixelKey}:${currentColor}`);
        return next;
      });
    }
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
  };

  const getPixelFromEvent = (e) => {
    if (!containerRef.current) return null;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.floor((e.clientX - rect.left) / (rect.width / gridSize));
    const y = Math.floor((e.clientY - rect.top) / (rect.height / gridSize));
    if (x >= 0 && x < gridSize && y >= 0 && y < gridSize) {
      return { x, y };
    }
    return null;
  };

  return (
    <motion.div 
      ref={containerRef}
      className="absolute inset-0 overflow-hidden rounded-xl"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      
      <AnimatePresence>
        {waitingToStart ? (
          <StartGameOverlay 
            onStart={handleStart}
            gameName="Pixel Drawer"
          />
        ) : (
          <>
            {/* Timer */}
            <div className="absolute top-4 right-4 text-white font-['VT323'] text-3xl">
              {timeLeft}s
            </div>

            {/* Color Picker */}
            <div className="absolute top-4 left-4 flex gap-2">
              {colors.map(color => (
                <motion.div
                  key={color}
                  className="w-6 h-6 rounded-full cursor-pointer"
                  style={{ 
                    backgroundColor: color,
                    boxShadow: currentColor === color ? `0 0 10px ${color}` : 'none'
                  }}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setCurrentColor(color)}
                />
              ))}
            </div>

            {/* Game Over */}
            {!gameStarted && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-white font-['VT323'] text-3xl">
                  Time's Up!
                </div>
              </div>
            )}

            {/* Pixel Grid */}
            <div 
              className="absolute inset-0 grid"
              style={{ 
                gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
                gap: '1px'
              }}
            >
              {Array.from({ length: gridSize * gridSize }, (_, i) => {
                const x = i % gridSize;
                const y = Math.floor(i / gridSize);
                const pixelColor = Array.from(pixels).find(p => p.startsWith(`${x},${y}:`))?.split(':')?.[1];
                
                return (
                  <motion.div
                    key={i}
                    className="w-full h-full"
                    style={{
                      backgroundColor: pixelColor || 'transparent',
                      opacity: pixelColor ? 1 : 0
                    }}
                    initial={{ scale: 0 }}
                    animate={{ scale: pixelColor ? 1 : 0 }}
                    transition={{ duration: 0.1 }}
                  />
                );
              })}
            </div>
          </>
        )}
      </AnimatePresence>


    </motion.div>
  );
};

export default RetroPixelDrawer; 